import type { ReactNode } from 'react';
import type { FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { useFieldIsRequired } from '~/components/Form';
import { cn } from '~/lib/cn';

import { FieldMessage } from '../FieldMessage';
import { Label } from '../Label';
import { assertNonEmptyName } from './assertions/nonEmptyName';

export interface FormFieldProps<TFieldValues extends FieldValues> {
  name: Path<TFieldValues>;
  label: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  triggerClassName?: string;
  showErrorMessage?: boolean;
  fieldMessageClassName?: string;
  children: ReactNode;

  /**
   * Disables the label HTML tag and uses span instead.
   * Useful when the inner content contains its own labels or multiple inputs.
   *
   * @default false
   */
  labelAsSpan?: boolean;

  /**
   * Optional addon to render inline with the label (e.g., info icon).
   */
  labelAddon?: ReactNode;
}

function FormField<TFieldValues extends FieldValues>({
  name,
  label,
  disabled,
  readOnly,
  className,
  children,
  labelAsSpan,
  labelAddon,
  fieldMessageClassName,
  showErrorMessage = true,
}: FormFieldProps<TFieldValues>) {
  assertNonEmptyName(name);

  const {
    fieldState: { error },
  } = useController<TFieldValues>({ name, disabled });

  const isRequired = useFieldIsRequired(name);

  return (
    <div className={cn('flex flex-col gap-sm', className)}>
      <Label
        id={name}
        disabled={disabled || readOnly}
        required={isRequired}
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        asSpan={labelAsSpan}
        name={name}
      >
        {label}
        {labelAddon}
      </Label>
      {children}
      {error && showErrorMessage ? (
        <FieldMessage className={fieldMessageClassName} state="invalid">
          {error.message}
        </FieldMessage>
      ) : null}
    </div>
  );
}

FormField.displayName = 'FormField';
export { FormField };
